import { Alert, Box, Button, Typography, useTheme } from '@mui/material';

import { useTranslate } from '@/hooks/useTranslate';

import { SourceType } from '@/types/document';

import { getAPIErrorMessage } from '@/utils/api';
import { urltoFile } from '@/utils/file';

import { Icon } from '../../../Icon/Icon';

import {
  DocumentCheckAction,
  DocumentLoader,
  StyledFileFrame,
} from './components.styled';

type DocumentPreviewProps = {
  title: string;
  description?: string;
  src: File | string;
  srcType?: SourceType;
  isPending?: boolean;
  pendingText?: string;
  error?: Error | null;
  onCancel?: () => void;
  cancelText?: string;
  onConfirm?: (payload: File) => void;
  confirmText?: string;
  isConfirmDisabled?: boolean;
};

export function DocumentPreview({
  title,
  description,
  src,
  srcType,
  isPending,
  pendingText,
  error,
  confirmText,
  cancelText,
  onCancel,
  onConfirm,
  isConfirmDisabled,
}: DocumentPreviewProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  const handleConfirm = async () => {
    let file: File;
    if (typeof src === 'string') {
      file = await urltoFile(src, 'document', 'image/jpg');
    } else {
      file = src;
    }
    onConfirm?.(file);
  };

  return (
    <>
      <Typography variant="h3" textAlign="center">
        {title}
      </Typography>
      {description && (
        <Typography
          mb={1}
          variant="body2"
          color={theme.palette.grey[800]}
          textAlign="center"
        >
          {description}
        </Typography>
      )}
      <StyledFileFrame src={src} srcType={srcType}>
        {isPending && (
          <DocumentLoader>
            <Typography variant="h5">
              {pendingText || t('common.loading')}
            </Typography>
          </DocumentLoader>
        )}
      </StyledFileFrame>
      {error && (
        <Box mt={2} alignSelf="stretch">
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        </Box>
      )}
      <DocumentCheckAction mt={3}>
        <Button
          variant="outlined"
          onClick={onCancel}
          fullWidth
          disabled={isPending}
        >
          {cancelText || t('common.cancel')}
        </Button>
        {onConfirm && (
          <Button
            fullWidth
            onClick={handleConfirm}
            disabled={isPending || isConfirmDisabled}
          >
            {confirmText || t('common.confirm')}
          </Button>
        )}
      </DocumentCheckAction>
    </>
  );
}
